import { defineStore } from 'pinia'

import { StoreState } from '@shared/models/interfaces/store'
import { Nullable, Optional } from '@shared/models/types/primary'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import {
  getInitialState,
  updateState,
} from '@shared/utils/store/http-request-state'

import { Conversation } from '@protos/apps/pc/conversation_pb'
import { Participant } from '@protos/chat/participant_pb'

import {
  listConversations,
  mergeConversation,
  mergeConversations,
} from '@pc/helpers/conversations'
import {
  ICreateDataConversation,
  IStartDataConversation,
} from '@pc/models/proto/base/conversations'
import {
  ConversationCreateDTO,
  ConversationShowDTO,
  ConversationsIndexDTO,
  ConversationStartDTO,
  ConversationUpdateDTO,
} from '@pc/models/proto/dto/conversations'
import {
  ConversationItemRO,
  ConversationsIndexRO,
} from '@pc/models/proto/ro/conversations'
import { useMessagesStore } from '@pc/store/messages'

type PaginationMeta = ConversationsIndexRO['meta']

interface State {
  conversations: StoreState<{
    data: Record<Conversation['id'], Conversation>
    meta: PaginationMeta
  }>
  currentConversationId: Nullable<number>
  isIframePage: boolean
}

const state = (): State => ({
  conversations: getInitialState(),
  currentConversationId: null,
  isIframePage: false,
})

export const apiTransport = new ApiTransport(resourcePath, 'pc')

export const useConversationsStore = defineStore('conversations', {
  state,

  actions: {
    async fetchConversations({
      page,
      chatWidgetId,
    }: Pick<ConversationsIndexDTO, 'chatWidgetId' | 'page'>) {
      this.conversations = updateState(this.conversations)

      try {
        const dto = {
          page,
          chatWidgetId,
          perPage: 20,
        }

        const response = await apiTransport.get<
          ConversationsIndexDTO,
          ConversationsIndexRO
        >(
          '/conversations',
          new ConversationsIndexDTO(dto),
          ConversationsIndexRO
        )

        const { meta, data: list } = response ?? { meta: {}, data: [] }

        const data = mergeConversations(list, this.conversations?.data?.data)
        this.conversations = updateState(this.conversations, { data, meta })
      } catch (error) {
        this.conversations = updateState(this.conversations, error as Error)
        throw error
      }
    },

    fetchConversationById: async function (dto: {
      conversationId: number
    }): Promise<Optional<Conversation>> {
      const messagesStore = useMessagesStore()
      messagesStore.resetSendMessageState()

      this.conversations = updateState(this.conversations)

      try {
        const response = await apiTransport.get<
          ConversationShowDTO,
          ConversationItemRO
        >(
          `/conversations/${dto.conversationId}`,
          new ConversationShowDTO(dto),
          ConversationItemRO
        )

        this.conversations = updateState(this.conversations, {
          data: mergeConversation(
            response?.data,
            this.conversations?.data?.data
          ),
          meta: this.conversations.data?.meta,
        })
        return response?.data
      } catch (e) {
        this.conversations = updateState(this.conversations, e as Error)

        throw e
      }
    },

    setCurrentConversation(id: Conversation['id']) {
      this.currentConversationId = id
    },

    async startConversation(dto: {
      conversationId: number
      data: IStartDataConversation
    }) {
      const messagesStore = useMessagesStore()
      messagesStore.resetSendMessageState()

      this.conversations = updateState(this.conversations)
      try {
        const response = await apiTransport.post<
          ConversationStartDTO,
          ConversationItemRO
        >(
          '/conversations/start',
          new ConversationStartDTO(dto),
          ConversationStartDTO,
          ConversationItemRO
        )

        const { data } = response ?? {}

        this.conversations = updateState(this.conversations, {
          data: mergeConversation(data, this.conversations?.data?.data),
          meta: this.conversations.data?.meta,
        })
      } catch (e) {
        this.conversations = updateState(this.conversations, e as Error)
        throw e
      }
    },

    async createConversation(data: ICreateDataConversation) {
      return await apiTransport.post<ConversationCreateDTO, ConversationItemRO>(
        '/conversations',
        new ConversationCreateDTO({ data }),
        ConversationCreateDTO,
        ConversationItemRO
      )
    },

    async escalateToAgentConversation(payload: { conversationId: number }) {
      return await apiTransport.put<ConversationUpdateDTO>(
        '/conversations/speak_to_human',
        new ConversationUpdateDTO(payload),
        ConversationUpdateDTO
      )
    },

    updateParticipantByChatUserId(
      chatUserId: Participant['chatUserId'],
      partialParticipant: Partial<Participant>
    ) {
      const currentConversation = this.currentConversation

      if (!currentConversation) {
        return
      }

      const currentParticipants = currentConversation.participants ?? []
      const currentAssignee = currentConversation.assignee

      const nextParticipants = currentParticipants.map((participant) => {
        if (participant.chatUserId === chatUserId) {
          return { ...participant, ...partialParticipant }
        }
        return participant
      })

      const nextAssignee =
        currentAssignee?.chatUserId === chatUserId
          ? { ...currentAssignee, ...partialParticipant }
          : currentAssignee

      const nextConversation = {
        ...currentConversation,
        assignee: nextAssignee,
        participants: nextParticipants,
      }

      nextConversation &&
        this.mergeToConversations(new Conversation(nextConversation))
    },
    mergeToConversations(conversation?: Conversation) {
      if (conversation) {
        this.conversations = updateState(this.conversations, {
          data: mergeConversation(
            new Conversation(conversation),
            this.conversations?.data?.data
          ),
          meta: this.conversations.data?.meta,
        })
      }
    },
  },
  getters: {
    currentConversation: (state) => {
      if (!state.currentConversationId || !state.conversations?.data?.data) {
        return null
      }
      return state.conversations.data.data[state.currentConversationId]
    },
    getConversationById: (state) => (id: number) => {
      if (!id || !state.conversations?.data?.data) {
        return null
      }
      return state.conversations.data.data[id] || null
    },
    allConversations: (state) => {
      return listConversations(state.conversations?.data?.data)
    },
    meta: (state) => state.conversations.data?.meta || ({} as PaginationMeta),
    isPending: (state) => state.conversations.status.pending,
  },
})
